/* eslint-disable @typescript-eslint/no-misused-promises */
import { useState, type ReactNode, type SyntheticEvent } from "react";
import type {
  AbilityRecord,
  FieldManifest,
} from "../../shared/api/generated/contracts";
import { useT } from "../../shared/i18n";
import { Button, Card, Dialog } from "../../shared/ui";
import { FieldControl, hiddenFieldIds, withoutKeys } from "../renderer/fields";

export interface ConflictState {
  current: AbilityRecord;
  attempted: Record<string, unknown>;
}

/** Card surface, or a plain group when the parent (a sheet) is the surface. */
function Surface({ bare, children }: { bare: boolean; children: ReactNode }) {
  return bare ? <div>{children}</div> : <Card>{children}</Card>;
}

export function RecordForm({
  fields: allFields,
  initial,
  onSubmit,
  onConflict,
  onDelete,
  onCancel,
  bare = false,
  stacked = false,
  /** Server diagnostics keyed by field id — each lands beside its own field. */
  fieldErrors,
}: {
  fields: FieldManifest[];
  initial: Record<string, unknown>;
  onSubmit: (payload: Record<string, unknown>) => Promise<void>;
  onConflict: (conflict: ConflictState) => void;
  onDelete?: () => void | Promise<void>;
  /** When set, Cancel joins Save in one equal-width row (create sheet). */
  onCancel?: () => void;
  /** Render without a Card surface (the create sheet already is one). */
  bare?: boolean;
  /** Full-width column: Save, Cancel, Delete (record page). */
  stacked?: boolean;
  fieldErrors?: Record<string, string>;
}) {
  const t = useT();
  // Hidden fields (ADR 0198) never render and their keys are never sent; the
  // server merges the stored hidden values back on update.
  const hiddenIds = hiddenFieldIds({ fields: allFields });
  const fields = allFields.filter((field) => !hiddenIds.has(field.id));
  const [values, setValues] = useState<Record<string, unknown>>(() => ({
    ...Object.fromEntries(
      fields
        .filter(
          (field) => field.default !== undefined || field.type === "boolean",
        )
        .map((field) => [field.id, field.default ?? false]),
    ),
    ...withoutKeys(initial, hiddenIds),
  }));
  const [requiredErrors, setRequiredErrors] = useState<Record<string, string>>(
    {},
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    // Guard against double submission: a second click while in flight is a
    // no-op rather than a second write.
    if (saving) return;
    const missing = Object.fromEntries(
      fields
        .filter((field) => {
          const value = values[field.id];
          return (
            field.required &&
            (value === undefined ||
              value === null ||
              (typeof value === "string" && !value.trim()) ||
              (Array.isArray(value) && value.length === 0))
          );
        })
        .map((field) => [field.id, t("record.required")]),
    );
    setRequiredErrors(missing);
    if (Object.keys(missing).length) return;
    setSaving(true);
    setError(null);
    try {
      await onSubmit(withoutKeys(values, hiddenIds));
    } catch (caught) {
      if (typeof caught === "object" && caught && "current" in caught)
        onConflict(caught as ConflictState);
      else
        setError("The save failed. Your values are still available for retry.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Surface bare={bare}>
      <form
        onSubmit={submit}
        aria-label={t("widget.recordForm")}
        className="flex flex-col gap-4"
      >
        {fields.map((field) => (
          <FieldControl
            key={field.id}
            field={field}
            value={values[field.id]}
            mode="edit"
            error={requiredErrors[field.id] ?? fieldErrors?.[field.id]}
            onChange={(next) => {
              setValues((current) => ({ ...current, [field.id]: next }));
            }}
          />
        ))}
        {/* A recoverable failure keeps the entered values in the form. */}
        {error && (
          <p role="alert" className="text-body font-semibold text-error">
            {error}
          </p>
        )}
        <div
          className={stacked ? "flex flex-col gap-2" : "flex flex-wrap gap-2"}
        >
          {stacked && (
            <Button type="submit" disabled={saving} className="w-full">
              {saving ? t("record.saving") : t("record.save")}
            </Button>
          )}
          {onCancel && (
            <Button
              variant="secondary"
              className={stacked ? "w-full" : "flex-1"}
              disabled={saving}
              onClick={onCancel}
            >
              {t("record.cancel")}
            </Button>
          )}
          {!stacked && (
            <Button
              type="submit"
              disabled={saving}
              className={onCancel ? "flex-1" : undefined}
            >
              {saving ? t("record.saving") : t("record.save")}
            </Button>
          )}
          {onDelete && (
            <Button
              variant={stacked ? "dangerSoft" : "destructive"}
              className={stacked ? "w-full" : undefined}
              disabled={saving}
              onClick={() => {
                setConfirmingDelete(true);
              }}
            >
              {t("record.delete")}
            </Button>
          )}
        </div>
      </form>
      {onDelete && (
        // Destructive commands never fire from the primary control: they always
        // pass through an explicit confirmation.
        <Dialog
          open={confirmingDelete}
          onOpenChange={setConfirmingDelete}
          title={t("record.deleteConfirm.title")}
          description={t("record.deleteConfirm.body")}
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setConfirmingDelete(false);
                }}
              >
                {t("record.cancel")}
              </Button>
              <Button
                variant="destructive"
                disabled={saving}
                onClick={async () => {
                  setSaving(true);
                  setError(null);
                  try {
                    await onDelete();
                    setConfirmingDelete(false);
                  } catch {
                    setError(t("record.error"));
                    setConfirmingDelete(false);
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                {t("record.deleteConfirm.confirm")}
              </Button>
            </>
          }
        />
      )}
    </Surface>
  );
}
